import { useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { parseQueryParamsFromRelativeUrlPath } from 'src/helpers';
import { OptionGroupsResponseBody } from 'src/api/getOptionGroups/types';
import { getOptionGroups } from 'src/api/getOptionGroups/getOptionGroups';
import { OptionGroupType } from 'src/constants';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';

interface UseOptionGroupsOptions {
  name?: string;
  limit?: number;
  type?: OptionGroupType[];
  ranges?: string[];
  menuIds?: string[];
  priceSchemeId?: string;
  missingDefaultPrice?: boolean;
  options?: UseInfiniteQueryOptions<OptionGroupsResponseBody>;
}

export const useOptionGroups = ({
  name,
  limit = 50,
  type,
  ranges,
  menuIds,
  priceSchemeId,
  missingDefaultPrice,
  options,
}: UseOptionGroupsOptions = {}) => {
  const { showSnackbar } = useSnackbar();
  const menuId = menuIds?.length ? [...menuIds].sort((a, b) => a.localeCompare(b)).join(',') : undefined;

  return useInfiniteQuery<OptionGroupsResponseBody>({
    queryKey: [QueryKeys.optionGroups, getBrandId(), name, type, ranges, menuId, priceSchemeId, missingDefaultPrice],
    queryFn: ({ pageParam }) =>
      getOptionGroups(pageParam, name, limit, type, ranges, menuId, priceSchemeId, missingDefaultPrice),
    getNextPageParam: (lastPage) => {
      const nextPageUrlPath = lastPage?.paging?.next;

      if (!nextPageUrlPath) return null;

      return parseQueryParamsFromRelativeUrlPath(nextPageUrlPath).get('after_id');
    },
    refetchOnWindowFocus: false,
    onError: (error) =>
      showSnackbar({ status: SnackbarStatus.ERROR, message: (error as Error)?.message || 'Internal Server Error' }),
    ...options,
  });
};

export default useOptionGroups;
